import { EventIdentifier, Logger, TrackingEventType, useAnalytics, useSettings } from '@capsicum/common';
import { useWCPledgeCancelPost } from '@capsicum/features/world-championship-pledge/shared';
import { useNCWBalance } from '@capsicum/socios-core';
import { useTransactionErrorSnackbar } from '@capsicum/socios-core-web';
import { useBrowserTransaction } from '@socios-blk/browser';
import { type PlatformTransactionExecutionParams } from '@socios-blk/core';
import { useEffect } from 'react';

type UseWCPledgeCancelTransactionParams = {
	ftName: string;
	onTransactionError: (error: Error) => void;
	onTransactionSuccess: () => void;
	pledgeContractAddress?: string;
	teamId: string;
};

export const useWCPledgeCancelTransaction = ({
	ftName,
	onTransactionError,
	onTransactionSuccess,
	pledgeContractAddress,
	teamId
}: UseWCPledgeCancelTransactionParams) => {
	const {
		settings: { isTransactionSponsored }
	} = useSettings();
	const { showTransactionErrorSnackbar } = useTransactionErrorSnackbar();
	const { formatCHZtoFiat } = useNCWBalance();
	const { cancelPledgeMutation, isPledgeCancelPosting } = useWCPledgeCancelPost();
	const { estimateGasFee, gasFeeEstimate, isError, isPending, sendTransaction, TRANSACTIONS } =
		useBrowserTransaction();
	const analytics = useAnalytics();

	const gasFeeEstimateFiat = typeof gasFeeEstimate === 'number' ? formatCHZtoFiat(gasFeeEstimate) : undefined;

	const paymasterParams: PlatformTransactionExecutionParams['paymasterParams'] = {
		isPaymasterConfigurationEnabled: isTransactionSponsored.worldChampionshipPledge,
		paymasterWebhookSecretParams: {
			parameters: [],
			target: 'world_championship_pledge'
		}
	};

	const buildPledgeCancelTransaction = () => {
		if (!pledgeContractAddress) {
			return undefined;
		}

		return TRANSACTIONS.CANCEL_PLEDGE({
			ftName,
			pledgeContractAddress
		});
	};

	const pledgeTransaction = buildPledgeCancelTransaction();

	useEffect(() => {
		if (gasFeeEstimate || !pledgeTransaction || isPending || isError) {
			return;
		}

		estimateGasFee({
			paymasterParams,
			transaction: [pledgeTransaction]
		});
	}, [gasFeeEstimate, pledgeTransaction, isPending, isError]);

	const sendCancelPledgeTransaction = async () => {
		if (!pledgeTransaction) {
			showTransactionErrorSnackbar();

			Logger.error(new Error('Pledge Cancel Transaction error'));

			return;
		}

		await sendTransaction({
			onError: err => {
				Logger.error(new Error(`Pledge Cancel Transaction error: ${err}`));
				onTransactionError(err);
			},
			onSuccess: async ok => {
				await cancelPledgeMutation({
					teamId,
					transactionHash: ok.transactionHash
				}).catch(error =>
					Logger.error('Error posting cancel pledge to BE', {
						error: error,
						transactionHash: ok.transactionHash
					})
				);
				onTransactionSuccess();
				analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
					action: 'wc_pledge_cancel',
					partner_name: ftName
				});
			},
			paymasterParams,
			transaction: [pledgeTransaction]
		});
	};

	return {
		gasFeeEstimate,
		gasFeeEstimateFiat,
		isTransactionPending: isPending || isPledgeCancelPosting,
		sendCancelPledgeTransaction
	};
};
